import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, Plus, Search, UserPlus, Building, Calendar, Clock,
  CheckCircle2, AlertCircle, UserCheck, Briefcase, FileText,
  MoreVertical, Trash2, ChevronRight, BarChart3, ClipboardList
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { AttendanceTab } from '../components/AttendanceTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVINCES = ["Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo", "Mpumalanga", "North West", "Northern Cape", "Western Cape"];
const EMP_TYPES = [{ v: "full_time", l: "Full Time" }, { v: "part_time", l: "Part Time" }, { v: "contract", l: "Contract" }, { v: "temporary", l: "Temporary" }, { v: "intern", l: "Intern" }];
const EMP_STATUSES = [{ v: "active", l: "Active" }, { v: "probation", l: "Probation" }, { v: "suspended", l: "Suspended" }, { v: "terminated", l: "Terminated" }, { v: "resigned", l: "Resigned" }];
const STATUS_COLORS = { active: 'bg-emerald-100 text-emerald-700', probation: 'bg-amber-100 text-amber-700', suspended: 'bg-red-100 text-red-700', terminated: 'bg-slate-100 text-slate-700', resigned: 'bg-slate-100 text-slate-700' };
const LEAVE_STATUS_COLORS = { pending: 'bg-amber-100 text-amber-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' };
const LEAVE_LABELS = { annual: "Annual", sick: "Sick", family_responsibility: "Family Resp.", maternity: "Maternity", paternity: "Paternity", study: "Study", unpaid: "Unpaid", compassionate: "Compassionate" };
const formatCurrency = (n) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0 }).format(n);

export const HRMPage = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Dialogs
  const [showEmpDialog, setShowEmpDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showDeptDialog, setShowDeptDialog] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(null);
  const [onboardingItems, setOnboardingItems] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Forms
  const emptyEmp = { first_name: '', last_name: '', email: '', phone: '', id_number: '', date_of_birth: '', gender: '', nationality: 'South African', department: '', position: '', employment_type: 'full_time', employment_status: 'active', start_date: '', province: '', basic_salary: 0, bank_name: '', bank_account_number: '', tax_number: '' };
  const emptyLeave = { employee_id: '', leave_type: 'annual', start_date: '', end_date: '', days_requested: 1, reason: '', half_day: false };
  const emptyDept = { name: '', code: '', description: '' };
  const [empForm, setEmpForm] = useState(emptyEmp);
  const [leaveForm, setLeaveForm] = useState(emptyLeave);
  const [deptForm, setDeptForm] = useState(emptyDept);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [dashRes, empRes, deptRes, leaveRes, typesRes] = await Promise.all([
        axios.get(`${API}/hrm/dashboard`), axios.get(`${API}/hrm/employees`),
        axios.get(`${API}/hrm/departments`), axios.get(`${API}/hrm/leave-requests`),
        axios.get(`${API}/hrm/leave-types`)
      ]);
      setDashboard(dashRes.data); setEmployees(empRes.data);
      setDepartments(deptRes.data); setLeaveRequests(leaveRes.data);
      setLeaveTypes(typesRes.data);
    } catch { toast.error('Failed to load HR data'); }
    finally { setLoading(false); }
  };

  const handleCreateEmployee = async () => {
    if (!empForm.first_name || !empForm.last_name) { toast.error('Name required'); return; }
    try {
      await axios.post(`${API}/hrm/employees`, empForm);
      toast.success('Employee added'); setShowEmpDialog(false); setEmpForm(emptyEmp); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Delete this employee?')) return;
    try { await axios.delete(`${API}/hrm/employees/${id}`); toast.success('Deleted'); loadAll(); } catch { toast.error('Failed'); }
  };

  const handleCreateDept = async () => {
    if (!deptForm.name) { toast.error('Name required'); return; }
    try { await axios.post(`${API}/hrm/departments`, deptForm); toast.success('Department created'); setShowDeptDialog(false); setDeptForm(emptyDept); loadAll(); } catch { toast.error('Failed'); }
  };

  const handleCreateLeave = async () => {
    if (!leaveForm.employee_id || !leaveForm.start_date || !leaveForm.end_date) { toast.error('Fill required fields'); return; }
    try { await axios.post(`${API}/hrm/leave-requests`, leaveForm); toast.success('Leave request submitted'); setShowLeaveDialog(false); setLeaveForm(emptyLeave); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleLeaveAction = async (id, status, reason) => {
    try { await axios.put(`${API}/hrm/leave-requests/${id}/approve`, { status, rejection_reason: reason }); toast.success(`Leave ${status}`); loadAll(); } catch { toast.error('Failed'); }
  };

  const loadOnboarding = async (empId) => {
    try { const res = await axios.get(`${API}/hrm/onboarding/${empId}`); setOnboardingItems(res.data.items || []); setShowOnboarding(empId); } catch { toast.error('Failed'); }
  };

  const toggleOnboardingItem = async (itemId) => {
    try { await axios.put(`${API}/hrm/onboarding/${itemId}/toggle`); loadOnboarding(showOnboarding); } catch { toast.error('Failed'); }
  };

  const filteredEmployees = employees.filter(e => {
    const q = search.toLowerCase();
    const matchSearch = !search || e.full_name?.toLowerCase().includes(q) || e.employee_number?.toLowerCase().includes(q) || e.email?.toLowerCase().includes(q);
    const matchDept = filterDept === 'all' || e.department === filterDept;
    const matchStatus = filterStatus === 'all' || e.employment_status === filterStatus;
    return matchSearch && matchDept && matchStatus;
  });

  const stats = dashboard?.stats || {};

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;

  // ===== RENDER DASHBOARD =====
  const renderDashboard = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: 'Total Employees', value: stats.total_employees, icon: Users, color: 'bg-blue-100 text-blue-600' },
          { label: 'Active', value: stats.active_employees, icon: UserCheck, color: 'bg-emerald-100 text-emerald-600' },
          { label: 'On Probation', value: stats.on_probation, icon: Clock, color: 'bg-amber-100 text-amber-600' },
          { label: 'Pending Leave', value: stats.pending_leaves, icon: Calendar, color: 'bg-purple-100 text-purple-600' },
          { label: 'On Leave', value: stats.employees_on_leave, icon: Briefcase, color: 'bg-teal-100 text-teal-600' },
          { label: 'Departments', value: stats.departments, icon: Building, color: 'bg-slate-100 text-slate-600' },
        ].map((s, i) => (
          <Card key={i}><CardContent className="p-4"><div className="flex items-center gap-3"><div className={`h-10 w-10 rounded-lg ${s.color} flex items-center justify-center`}><s.icon className="h-5 w-5" /></div><div><p className="text-xs text-muted-foreground">{s.label}</p><p className="text-xl font-bold font-mono">{s.value}</p></div></div></CardContent></Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card><CardHeader><CardTitle className="text-base">Recent Hires</CardTitle></CardHeader><CardContent>
          {(dashboard?.recent_hires || []).length === 0 ? <p className="text-muted-foreground text-sm">No recent hires</p> : (
            <div className="space-y-3">{(dashboard?.recent_hires || []).map(e => (
              <div key={e.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div><p className="font-medium text-sm">{e.full_name}</p><p className="text-xs text-muted-foreground">{e.department} - {e.position}</p></div>
                <Badge className={STATUS_COLORS[e.employment_status]}>{e.employment_status}</Badge>
              </div>
            ))}</div>
          )}
        </CardContent></Card>
        <Card><CardHeader><CardTitle className="text-base">Pending Leave Requests</CardTitle></CardHeader><CardContent>
          {(dashboard?.pending_leave_requests || []).length === 0 ? <p className="text-muted-foreground text-sm">No pending requests</p> : (
            <div className="space-y-3">{(dashboard?.pending_leave_requests || []).map(r => (
              <div key={r.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div><p className="font-medium text-sm">{r.employee_name}</p><p className="text-xs text-muted-foreground">{LEAVE_LABELS[r.leave_type]} - {r.days_requested} days ({r.start_date} to {r.end_date})</p></div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600" onClick={() => handleLeaveAction(r.id, 'approved')}>Approve</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => handleLeaveAction(r.id, 'rejected', 'Declined by manager')}>Reject</Button>
                </div>
              </div>
            ))}</div>
          )}
        </CardContent></Card>
      </div>
      {Object.keys(dashboard?.department_breakdown || {}).length > 0 && (
        <Card><CardHeader><CardTitle className="text-base">Department Breakdown</CardTitle></CardHeader><CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(dashboard.department_breakdown).map(([name, count]) => (
              <div key={name} className="p-3 bg-muted/50 rounded-lg text-center"><p className="text-2xl font-bold font-mono">{count}</p><p className="text-xs text-muted-foreground">{name}</p></div>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );

  // ===== RENDER EMPLOYEES =====
  const renderEmployees = () => (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search employees..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} data-testid="employee-search" /></div>
        <Select value={filterDept} onValueChange={setFilterDept}><SelectTrigger className="w-[160px]"><SelectValue placeholder="Department" /></SelectTrigger><SelectContent><SelectItem value="all">All Departments</SelectItem>{departments.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent></Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{EMP_STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select>
        <Button onClick={() => { setEmpForm(emptyEmp); setSelectedEmployee(null); setShowEmpDialog(true); }} data-testid="add-employee-btn"><UserPlus className="h-4 w-4 mr-2" />Add Employee</Button>
      </div>
      <Card><ScrollArea className="max-h-[600px]"><Table>
        <TableHeader><TableRow>
          <TableHead>Employee #</TableHead><TableHead>Name</TableHead><TableHead>Department</TableHead>
          <TableHead>Position</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead>
          <TableHead>Start Date</TableHead><TableHead className="w-10"></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {filteredEmployees.map(emp => (
            <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
              <TableCell className="font-mono text-sm">{emp.employee_number}</TableCell>
              <TableCell><div><p className="font-medium">{emp.full_name}</p>{emp.email && <p className="text-xs text-muted-foreground">{emp.email}</p>}</div></TableCell>
              <TableCell className="text-sm">{emp.department || '-'}</TableCell>
              <TableCell className="text-sm">{emp.position || '-'}</TableCell>
              <TableCell><Badge variant="outline" className="text-xs capitalize">{emp.employment_type?.replace('_', ' ')}</Badge></TableCell>
              <TableCell><Badge className={STATUS_COLORS[emp.employment_status] || 'bg-slate-100 text-slate-700'}>{emp.employment_status}</Badge></TableCell>
              <TableCell className="text-sm">{emp.start_date || '-'}</TableCell>
              <TableCell>
                <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => { setSelectedEmployee(emp); setEmpForm(emp); setShowEmpDialog(true); }}>Edit</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => loadOnboarding(emp.id)}><ClipboardList className="h-4 w-4 mr-2" />Onboarding</DropdownMenuItem>
                    <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteEmployee(emp.id)}>Delete</DropdownMenuItem>
                  </DropdownMenuContent></DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
          {filteredEmployees.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No employees found</TableCell></TableRow>}
        </TableBody>
      </Table></ScrollArea></Card>
    </div>
  );

  // ===== RENDER LEAVE =====
  const renderLeave = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Leave Requests ({leaveRequests.length})</h3>
        <Button onClick={() => setShowLeaveDialog(true)} data-testid="request-leave-btn"><Calendar className="h-4 w-4 mr-2" />Request Leave</Button>
      </div>
      <Card><ScrollArea className="max-h-[600px]"><Table>
        <TableHeader><TableRow>
          <TableHead>Employee</TableHead><TableHead>Type</TableHead><TableHead>Period</TableHead>
          <TableHead>Days</TableHead><TableHead>Reason</TableHead><TableHead>Status</TableHead><TableHead className="w-[120px]">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {leaveRequests.map(req => (
            <TableRow key={req.id}>
              <TableCell><div><p className="font-medium text-sm">{req.employee_name || 'Unknown'}</p><p className="text-xs text-muted-foreground">{req.department}</p></div></TableCell>
              <TableCell><Badge variant="outline" className="text-xs">{LEAVE_LABELS[req.leave_type] || req.leave_type}</Badge></TableCell>
              <TableCell className="text-sm">{req.start_date} - {req.end_date}</TableCell>
              <TableCell className="font-mono text-sm">{req.days_requested}</TableCell>
              <TableCell className="text-sm max-w-[200px] truncate">{req.reason || '-'}</TableCell>
              <TableCell><Badge className={LEAVE_STATUS_COLORS[req.status]}>{req.status}</Badge></TableCell>
              <TableCell>
                {req.status === 'pending' && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600" onClick={() => handleLeaveAction(req.id, 'approved')}>Approve</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => handleLeaveAction(req.id, 'rejected', 'Declined')}>Reject</Button>
                  </div>
                )}
                {req.status !== 'pending' && <span className="text-xs text-muted-foreground">{req.approved_by_name || '-'}</span>}
              </TableCell>
            </TableRow>
          ))}
          {leaveRequests.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No leave requests</TableCell></TableRow>}
        </TableBody>
      </Table></ScrollArea></Card>
    </div>
  );

  // ===== RENDER DEPARTMENTS =====
  const renderDepartments = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold">Departments ({departments.length})</h3>
        <Button onClick={() => setShowDeptDialog(true)} data-testid="add-dept-btn"><Plus className="h-4 w-4 mr-2" />Add Department</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {departments.map(dept => (
          <Card key={dept.id}><CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center"><Building className="h-5 w-5 text-blue-600" /></div>
                <div><p className="font-semibold">{dept.name}</p>{dept.code && <p className="text-xs text-muted-foreground">{dept.code}</p>}</div>
              </div>
            </div>
            {dept.description && <p className="text-sm text-muted-foreground mb-3">{dept.description}</p>}
            <div className="flex items-center justify-between pt-3 border-t">
              <div className="flex items-center gap-2"><Users className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-mono">{dept.employee_count || 0} employees</span></div>
            </div>
          </CardContent></Card>
        ))}
        {departments.length === 0 && <Card className="col-span-full"><CardContent className="p-12 text-center text-muted-foreground">No departments created yet</CardContent></Card>}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="hrm-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Human Resources</h1>
          <p className="text-muted-foreground mt-1">Employee management, leave & compliance</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="hr-dashboard-tab"><BarChart3 className="h-3 w-3 mr-1" />Dashboard</TabsTrigger>
          <TabsTrigger value="employees" data-testid="hr-employees-tab"><Users className="h-3 w-3 mr-1" />Employees ({employees.length})</TabsTrigger>
          <TabsTrigger value="leave" data-testid="hr-leave-tab"><Calendar className="h-3 w-3 mr-1" />Leave ({leaveRequests.filter(r => r.status === 'pending').length} pending)</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="hr-attendance-tab"><Clock className="h-3 w-3 mr-1" />Attendance</TabsTrigger>
          <TabsTrigger value="departments" data-testid="hr-departments-tab"><Building className="h-3 w-3 mr-1" />Departments</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">{renderDashboard()}</TabsContent>
        <TabsContent value="employees">{renderEmployees()}</TabsContent>
        <TabsContent value="leave">{renderLeave()}</TabsContent>
        <TabsContent value="attendance"><AttendanceTab employees={employees} /></TabsContent>
        <TabsContent value="departments">{renderDepartments()}</TabsContent>
      </Tabs>

      {/* ===== EMPLOYEE DIALOG ===== */}
      <Dialog open={showEmpDialog} onOpenChange={setShowEmpDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedEmployee ? 'Edit Employee' : 'Add Employee'}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>First Name *</Label><Input value={empForm.first_name} onChange={e => setEmpForm({...empForm, first_name: e.target.value})} data-testid="emp-first-name" /></div>
              <div className="space-y-1"><Label>Last Name *</Label><Input value={empForm.last_name} onChange={e => setEmpForm({...empForm, last_name: e.target.value})} data-testid="emp-last-name" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Email</Label><Input type="email" value={empForm.email} onChange={e => setEmpForm({...empForm, email: e.target.value})} /></div>
              <div className="space-y-1"><Label>Phone</Label><Input value={empForm.phone} onChange={e => setEmpForm({...empForm, phone: e.target.value})} placeholder="+27..." /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>ID Number</Label><Input value={empForm.id_number} onChange={e => setEmpForm({...empForm, id_number: e.target.value})} /></div>
              <div className="space-y-1"><Label>Date of Birth</Label><Input type="date" value={empForm.date_of_birth} onChange={e => setEmpForm({...empForm, date_of_birth: e.target.value})} /></div>
              <div className="space-y-1"><Label>Gender</Label>
                <Select value={empForm.gender} onValueChange={v => setEmpForm({...empForm, gender: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
            </div>
            <div className="border-t pt-3"><h4 className="text-sm font-semibold mb-2">Employment Details</h4></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Department</Label>
                <Select value={empForm.department} onValueChange={v => setEmpForm({...empForm, department: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{departments.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label>Position</Label><Input value={empForm.position} onChange={e => setEmpForm({...empForm, position: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Type</Label>
                <Select value={empForm.employment_type} onValueChange={v => setEmpForm({...empForm, employment_type: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{EMP_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label>Status</Label>
                <Select value={empForm.employment_status} onValueChange={v => setEmpForm({...empForm, employment_status: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{EMP_STATUSES.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label>Start Date</Label><Input type="date" value={empForm.start_date} onChange={e => setEmpForm({...empForm, start_date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Province</Label>
                <Select value={empForm.province} onValueChange={v => setEmpForm({...empForm, province: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{PROVINCES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label>Basic Salary (ZAR)</Label><Input type="number" value={empForm.basic_salary} onChange={e => setEmpForm({...empForm, basic_salary: parseFloat(e.target.value) || 0})} /></div>
            </div>
            <div className="border-t pt-3"><h4 className="text-sm font-semibold mb-2">Banking & Tax</h4></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label>Bank</Label><Input value={empForm.bank_name} onChange={e => setEmpForm({...empForm, bank_name: e.target.value})} /></div>
              <div className="space-y-1"><Label>Account #</Label><Input value={empForm.bank_account_number} onChange={e => setEmpForm({...empForm, bank_account_number: e.target.value})} /></div>
              <div className="space-y-1"><Label>Tax Number</Label><Input value={empForm.tax_number} onChange={e => setEmpForm({...empForm, tax_number: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowEmpDialog(false)}>Cancel</Button><Button onClick={handleCreateEmployee} data-testid="save-employee-btn">{selectedEmployee ? 'Update' : 'Add Employee'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== LEAVE DIALOG ===== */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent><DialogHeader><DialogTitle>Request Leave</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="space-y-1"><Label>Employee *</Label>
              <Select value={leaveForm.employee_id} onValueChange={v => setLeaveForm({...leaveForm, employee_id: v})}><SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1"><Label>Leave Type *</Label>
              <Select value={leaveForm.leave_type} onValueChange={v => setLeaveForm({...leaveForm, leave_type: v})}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(LEAVE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Start Date *</Label><Input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm({...leaveForm, start_date: e.target.value})} /></div>
              <div className="space-y-1"><Label>End Date *</Label><Input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm({...leaveForm, end_date: e.target.value})} /></div>
            </div>
            <div className="space-y-1"><Label>Days Requested</Label><Input type="number" min="0.5" step="0.5" value={leaveForm.days_requested} onChange={e => setLeaveForm({...leaveForm, days_requested: parseFloat(e.target.value) || 0})} /></div>
            <div className="space-y-1"><Label>Reason</Label><Textarea value={leaveForm.reason} onChange={e => setLeaveForm({...leaveForm, reason: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowLeaveDialog(false)}>Cancel</Button><Button onClick={handleCreateLeave} data-testid="submit-leave-btn">Submit Request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DEPARTMENT DIALOG ===== */}
      <Dialog open={showDeptDialog} onOpenChange={setShowDeptDialog}>
        <DialogContent><DialogHeader><DialogTitle>Add Department</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Name *</Label><Input value={deptForm.name} onChange={e => setDeptForm({...deptForm, name: e.target.value})} data-testid="dept-name-input" /></div>
              <div className="space-y-1"><Label>Code</Label><Input value={deptForm.code} onChange={e => setDeptForm({...deptForm, code: e.target.value})} placeholder="e.g. HR, IT" /></div>
            </div>
            <div className="space-y-1"><Label>Description</Label><Textarea value={deptForm.description} onChange={e => setDeptForm({...deptForm, description: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowDeptDialog(false)}>Cancel</Button><Button onClick={handleCreateDept} data-testid="save-dept-btn">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== ONBOARDING DIALOG ===== */}
      <Dialog open={!!showOnboarding} onOpenChange={() => setShowOnboarding(null)}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Onboarding Checklist</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {onboardingItems.map(item => (
              <div key={item.id} className={`flex items-center gap-3 p-3 rounded-lg ${item.completed ? 'bg-emerald-50' : 'bg-muted/50'}`}>
                <Checkbox checked={item.completed} onCheckedChange={() => toggleOnboardingItem(item.id)} />
                <div className="flex-1">
                  <p className={`text-sm ${item.completed ? 'line-through text-muted-foreground' : 'font-medium'}`}>{item.title}</p>
                  <Badge variant="outline" className="text-[10px] mt-0.5">{item.category}</Badge>
                </div>
                {item.completed && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
              </div>
            ))}
            {onboardingItems.length === 0 && <p className="text-center text-muted-foreground py-8">No checklist items</p>}
          </div>
          <DialogFooter>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" /> {onboardingItems.filter(i => i.completed).length}/{onboardingItems.length} completed
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRMPage;
